import {
  AppointmentApi,
  AppointmentRequestApi,
  AsyncAppointmentApi,
  AuthenticationApi,
  AvailabilityApi,
  BenefitsApi,
  BillingApi,
  CareTeamApi,
  ChatApi,
  ChartingApi,
  ClinicalAdminApi,
  ClinicalAppointmentApi,
  CommentsApi,
  ConsentApi,
  CompanyAdminApi,
  CompanyApi,
  CompanyMemberApi,
  EPrescribeAuthApi,
  EncounterApi,
  EntitlementApi,
  FeaturePolicyApi,
  HouseholdApi,
  LabOrderingApi,
  MedicalApi,
  MemberApi,
  MembershipApi,
  NotificationPreferencesApi,
  OnsiteClinicApi,
  PBMApi,
  PatientConversationApi,
  ProviderApi,
  QuestionnairesApi,
  SurveyApi,
  TaskApi,
  UrgentCareOrderingApi,
  UserApi,
  UtilityApi,
  YouthMembershipApi,
  AsclepiusAppErrorCodeEnum,
  AppNameEnum
} from "@vitable/asclepius-api-client-ts"

import { SERVER_URL } from "@/config"
import { version } from "../../../package.json"
import axios, { type AxiosInstance } from "axios"
// @ts-ignore
import store from "@/store"
import { useStateFlow } from "@/composables/api/state-flow"

const APP_NAME: AppNameEnum = AppNameEnum.ProviderDashboard
const APP_VERSION: string = version

/**
 * Specialization of {@link Error}.
 * Base class for all Asclepius API-related errors.
 */
export class AsclepiusApiError extends Error {
  public appErrorCode: string | null
  public cause: any
  public sendToSentryIn: boolean

  constructor(cause: Error, message: string, appErrorCode: string | null, sendToSentryIn: boolean) {
    super(message)
    this.cause = cause
    this.appErrorCode = appErrorCode
    this.sendToSentryIn = sendToSentryIn
  }
}

/**
 * Specialization of {@link AsclepiusApiError}.
 * Indicates that the Asclepius API returned back a non-2XX response.
 */
export class AsclepiusApiResponseError extends AsclepiusApiError {
  public serverTimestamp: string
  public traceId: string

  constructor(cause: Error, message: string, appErrorCode: string | null, traceId: string, serverTimestamp: string) {
    super(cause, message, appErrorCode, false) // Asclepius already reports these when needed
    this.traceId = traceId
    this.serverTimestamp = serverTimestamp
  }
}

export const useAsclepius = () => {
  let refreshAccessTokenPromise: Promise<any> | null = null

  // All API clients below will share / use this axios instance
  const axiosInstance: AxiosInstance = axios.create({
    baseURL: SERVER_URL,
    headers: { "Content-Type": "application/json" }
  })

  // This request interceptor will inject the access token for each request
  axiosInstance.interceptors.request.use(
    (request) => {
      const requestUrl: string = request.url ?? ""

      const callingAnAuthenticationApiIn: boolean =
        requestUrl.includes("auth/standard") ||
        requestUrl.includes("auth/passwordless") ||
        requestUrl.includes("auth/demographic") ||
        requestUrl.includes("auth/renewal") ||
        // V2 login plane ("auth/password" also covers "auth/passwordless")
        requestUrl.includes("auth/password") ||
        requestUrl.includes("auth/refresh")

      // Don't set the access token for authentication APIs
      const accessToken = store.getters["me/token"]
      if (accessToken && !callingAnAuthenticationApiIn) {
        console.debug(`[Axios/Request/onFulfilled] Injecting access token into request… ${requestUrl}`)
        request.headers["Authorization"] = `JWT ${accessToken}`
      }

      return request
    },
    (error) => Promise.reject(error)
  )

  // This response interceptor will automatically refresh expired access tokens
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Cancelled requests carry no config or response; pass them through untouched.
      if (axios.isCancel(error)) return Promise.reject(error)

      const originalRequestUrl: string = error.config.url ?? ""
      const originalRequest = error.config
      console.debug(`[Axios/Response/onRejected] Status ${error.response?.status} - ${originalRequestUrl}`)

      const refreshToken = store.getters["me/refreshToken"]
      const isRequestRetriable = !originalRequest._retry
      const isTokenExpired = error.response?.data.app_error_code === AsclepiusAppErrorCodeEnum.InvalidAccessToken

      if (refreshToken && isRequestRetriable && isTokenExpired) {
        console.debug(`[Axios/Response/onRejected] Expired access token. Attempting to refresh… ${originalRequestUrl}`)
        originalRequest._retry = true // Mark the request as retried to avoid infinite loops

        // If a refresh is already in progress, just wait for it (prevents concurrent refreshing)
        if (refreshAccessTokenPromise) {
          await refreshAccessTokenPromise
          return axiosInstance(originalRequest)
        }

        try {
          refreshAccessTokenPromise = (async () => {
            const tempAuthApi = new AuthenticationApi(undefined, SERVER_URL)
            const session = (
              await tempAuthApi.refreshSession({
                refreshSessionRequest: { refresh_token: refreshToken, app_name: APP_NAME, app_version: APP_VERSION }
              })
            ).data

            // Store the new access and refresh tokens
            const newAccessToken: string = session.access_token
            const newRefreshToken: string = session.refresh_token
            store.dispatch("me/updateAuthTokens", { accessToken: newAccessToken, refreshToken: newRefreshToken })

            // Update the Authorization header with the new access token and retry
            originalRequest.headers["Authorization"] = `JWT ${newAccessToken}`
            axiosInstance.defaults.headers.common["Authorization"] = `JWT ${newAccessToken}`
            console.debug("[Axios/Response/onRejected] Successfully refreshed access token.")
          })()

          await refreshAccessTokenPromise
          return axiosInstance(originalRequest)
        } catch (refreshError) {
          // A REJECTED session (invalid/rotated-out refresh token, disabled
          // user) is terminal; anything else is transient — a network blip
          // must not log the provider out mid-shift
          const refreshErrorCode = (refreshError as any)?.response?.data?.app_error_code
          const isTerminalRejection =
            refreshErrorCode === AsclepiusAppErrorCodeEnum.InvalidRefreshToken ||
            refreshErrorCode === AsclepiusAppErrorCodeEnum.UserDisabled

          if (isTerminalRejection) {
            console.debug("[Axios/Response/onRejected] Expired refresh token… need to login again")
            await store.dispatch("me/logout")
            window.location.reload()
          }

          return Promise.reject(refreshError)
        } finally {
          refreshAccessTokenPromise = null
        }
      }

      return Promise.reject(error) // For all other errors, return the error as is
    }
  )

  const getAppointmentApi = () => new AppointmentApi(undefined, SERVER_URL, axiosInstance)
  const getAppointmentRequestApi = () => new AppointmentRequestApi(undefined, SERVER_URL, axiosInstance)
  const getAuthenticationApi = () => new AuthenticationApi(undefined, SERVER_URL, axiosInstance)
  const getAvailabilityApi = () => new AvailabilityApi(undefined, SERVER_URL, axiosInstance)
  const getEncounterApi = () => new EncounterApi(undefined, SERVER_URL, axiosInstance)
  const getFeaturePolicyApi = () => new FeaturePolicyApi(undefined, SERVER_URL, axiosInstance)
  const getChatApi = () => new ChatApi(undefined, SERVER_URL, axiosInstance)
  const getCompanyApi = () => new CompanyApi(undefined, SERVER_URL, axiosInstance)
  const getCompanyMemberApi = () => new CompanyMemberApi(undefined, SERVER_URL, axiosInstance)
  const getClinicalAdminApi = () => new ClinicalAdminApi(undefined, SERVER_URL, axiosInstance)
  const getClinicalAppointmentApi = () => new ClinicalAppointmentApi(undefined, SERVER_URL, axiosInstance)
  const getHouseholdApi = () => new HouseholdApi(undefined, SERVER_URL, axiosInstance)
  const getUserApi = () => new UserApi(undefined, SERVER_URL, axiosInstance)
  const getNotificationPreferencesApi = () => new NotificationPreferencesApi(undefined, SERVER_URL, axiosInstance)
  const getMemberApi = () => new MemberApi(undefined, SERVER_URL, axiosInstance)
  const getMembershipApi = () => new MembershipApi(undefined, SERVER_URL, axiosInstance)
  const getBillingApi = () => new BillingApi(undefined, SERVER_URL, axiosInstance)
  const getMedicalApi = () => new MedicalApi(undefined, SERVER_URL, axiosInstance)
  const getOnsiteClinicApi = () => new OnsiteClinicApi(undefined, SERVER_URL, axiosInstance)
  const getCompanyAdminApi = () => new CompanyAdminApi(undefined, SERVER_URL, axiosInstance)
  const getSurveyApi = () => new SurveyApi(undefined, SERVER_URL, axiosInstance)
  const getLabOrderingApi = () => new LabOrderingApi(undefined, SERVER_URL, axiosInstance)
  const getUrgentCareOrderingApi = () => new UrgentCareOrderingApi(undefined, SERVER_URL, axiosInstance)
  const getCommentsApi = () => new CommentsApi(undefined, SERVER_URL, axiosInstance)
  const getConsentApi = () => new ConsentApi(undefined, SERVER_URL, axiosInstance)
  const getTaskApi = () => new TaskApi(undefined, SERVER_URL, axiosInstance)
  const getUtilityApi = () => new UtilityApi(undefined, SERVER_URL, axiosInstance)
  const getProviderApi = () => new ProviderApi(undefined, SERVER_URL, axiosInstance)
  const getBenefitsApi = () => new BenefitsApi(undefined, SERVER_URL, axiosInstance)
  const getChartingApi = () => new ChartingApi(undefined, SERVER_URL, axiosInstance)
  const getEntitlementApi = () => new EntitlementApi(undefined, SERVER_URL, axiosInstance)
  const getYouthMembershipApi = () => new YouthMembershipApi(undefined, SERVER_URL, axiosInstance)
  const getPbmApi = () => new PBMApi(undefined, SERVER_URL, axiosInstance)
  const getQuestionnairesApi = () => new QuestionnairesApi(undefined, SERVER_URL, axiosInstance)
  const getAsyncAppointmentApi = () => new AsyncAppointmentApi(undefined, SERVER_URL, axiosInstance)
  const getCareTeamApi = () => new CareTeamApi(undefined, SERVER_URL, axiosInstance)
  const getEPrescribeAuthApi = () => new EPrescribeAuthApi(undefined, SERVER_URL, axiosInstance)
  const getPatientConversationApi = () => new PatientConversationApi(undefined, SERVER_URL, axiosInstance)

  const { result, setLoadingResult, setFailureResult, setSuccessResult } = useStateFlow()

  async function callWithResponse<T>(fn: () => Promise<{ data: T }>): Promise<T> {
    setLoadingResult()
    try {
      const data: T = (await fn()).data
      setSuccessResult<T>(data)
      return data
    } catch (e: any) {
      const cause: Error = translateAsclepiusApiCallError(e)
      setFailureResult(cause)
      throw cause
    }
  }

  return {
    callWithResponse,
    callResult: result,
    getAppointmentApi,
    getAppointmentRequestApi,
    getAuthenticationApi,
    getEncounterApi,
    getChatApi,
    getCompanyApi,
    getCompanyMemberApi,
    getClinicalAdminApi,
    getClinicalAppointmentApi,
    getHouseholdApi,
    getFeaturePolicyApi,
    getUserApi,
    getMembershipApi,
    getNotificationPreferencesApi,
    getMemberApi,
    getBillingApi,
    getMedicalApi,
    getCompanyAdminApi,
    getOnsiteClinicApi,
    getSurveyApi,
    getLabOrderingApi,
    getUrgentCareOrderingApi,
    getCommentsApi,
    getConsentApi,
    getTaskApi,
    getUtilityApi,
    getAvailabilityApi,
    getProviderApi,
    getQuestionnairesApi,
    getBenefitsApi,
    getEntitlementApi,
    getChartingApi,
    getYouthMembershipApi,
    getPbmApi,
    getAsyncAppointmentApi,
    getCareTeamApi,
    getEPrescribeAuthApi,
    getPatientConversationApi
  }
}

/**
 * Translates Axios errors from Asclepius API calls into structured error objects
 * with user-friendly messages and appropriate Sentry reporting behavior.
 */
function translateAsclepiusApiCallError(ex: Error): Error {
  if (!axios.isAxiosError(ex)) {
    return ex
  }

  // For server-side problems, ensure that these are ignored on Sentry (Asclepius
  // will have reported it already if necessary) and ensure that these errors are
  // legible.
  const asclepiusErrorData = ex.response?.data
  if (asclepiusErrorData) {
    return new AsclepiusApiResponseError(
      ex,
      asclepiusErrorData["message"],
      asclepiusErrorData["app_error_code"],
      asclepiusErrorData["trace_id"],
      asclepiusErrorData["timestamp"]
    )
  }

  // For network connectivity problems, ensure that these are ignored on
  // Sentry (user probably just needs to connect to WI-FI) and ensure that
  // these errors are legible.
  if (ex.message == "Network Error") {
    return new AsclepiusApiError(
      ex,
      "Cannot connect to Vitable system… are you connected to the internet?",
      "network",
      false
    )
  }

  // For any other unknown problems, ensure that these do go to Sentry
  // because something weird is happening on the client-side that may
  // need fixed and may not be reported to Sentry by Asclepius
  return ex
}

/**
 * Determines if the given error is an ignorable Asclepius error.
 * @param ex the error to be checked
 *
 * @returns {@code true} if it should be ignored; otherwise {@code false}
 */
export function isIgnorableAsclepiusApiError(ex: unknown): boolean {
  return ex instanceof AsclepiusApiError && !ex.sendToSentryIn
}
